<template>
  <el-dialog :visible.sync="visibleSync" :width="step === 1 ? '30%' : '50%'" title="Import phân loại giảng viên">
    <div v-if="step === 1">
      <div>
        <span class="d-block">Bước 1: Tải file mẫu</span>
        <button class="mt-2 btn btn-primary font-weight-bold mr-2" @click="downloadTemplate">
          <i class="fas fa-download"></i> Tải xuống
        </button>
      </div>
      <div class="mt-5">
        <span class="d-block">Bước 2: Tải file excel lên hệ thống</span>
        <div class="file-input">
          <input ref="fileInput" type="file" class="file-input__input"
            accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            @change="onFile" :disabled="!!file" />
          <label class="file-input__label mt-2" :class="{ 'is-disabled': !!file }" for="file-input-team"
            @click.prevent="triggerFile">
            <span><i class="fas fa-upload mr-2" style="color:#fff"></i>Tải lên</span>
          </label>
          <div v-if="file" class="mt-2">
            <span class="badge badge-secondary p-2">{{ file.name }}</span>
            <i class="fas fa-times ml-2 text-danger" style="cursor:pointer" @click="clearFile"></i>
          </div>
          <div v-else class="text-danger mt-2">Vui lòng chọn file</div>
        </div>
      </div>
    </div>
    <div v-else>
      <p>Hệ thống phát hiện có lỗi trong quá trình import dữ liệu:</p>
      <div class="mt-3 table-responsive">
        <table class="table table-bordered table-vertical-center">
          <thead>
            <tr style="background-color:#F8F8F8;font-size:13px">
              <th>Mã giảng viên</th>
              <th>Phân loại</th>
              <th>Kết quả</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(r, idx) in results" :key="idx">
              <td>{{ r.teacher_code }}</td>
              <td>{{ r.category }}</td>
              <td>
                <span v-if="r.ok" style="color: #038E00; font-weight: bold;">Đạt</span>
                <div v-else>
                  <p class="text-danger" v-for="(e, i) in r.errors" :key="i">{{ e }}</p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="d-flex justify-content-end">
        <button type="button" class="btn btn-secondary" @click="step = 1">Quay lại</button>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <button class="btn btn-secondary mr-2" @click="emitClose">Hủy</button>
      <button v-if="step === 1" class="btn btn-info d-inline-flex align-items-center" :disabled="!file || loading"
        @click="submit">

        <span v-if="loading" class="spinner-border spinner-border-sm mr-2" role="status" aria-hidden="true"></span>
        <span>Xác nhận</span>

      </button>
    </span>
  </el-dialog>
</template>

<script>
import { INSPECTION_TEACHER_CATEGORY_DOWNLOAD, INSPECTION_TEACHER_CATEGORY_IMPORT, INSPECTION_TEACHER_PREVIEW_IMPORT } from '@/core/services/store/inspection/inspection.module';
import { utils, read } from 'xlsx';

export default {
  name: 'TeacherCategoryImport',
  props: { visible: { type: Boolean, default: false } },
  data() {
    return {
      visibleSync: this.visible,
      file: null,
      importRows: [],
      results: [],
      step: 1,
      loading: false,
    };
  },
  watch: { visible(v) { this.visibleSync = v; }, visibleSync(v) { this.$emit('update:visible', v); } },
  methods: {
    emitClose() {
      this.$emit('close');
      this.visibleSync = false;
    },
    triggerFile() { if (!this.file) this.$refs.fileInput.click(); },
    clearFile() { this.file = null; this.importRows = []; this.$refs.fileInput.value = null; },
    async downloadTemplate() { await this.$store.dispatch(INSPECTION_TEACHER_CATEGORY_DOWNLOAD); },
    onFile(e) {
      const f = e.target.files[0];
      if (!f) return;

      // Kiểm tra loại file
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];
      const validExtensions = ['.xls', '.xlsx'];
      const fileName = f.name.toLowerCase();
      const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));

      if (!validTypes.includes(f.type) && !hasValidExtension) {
        this.$message.error('File không hợp lệ. Vui lòng chọn file Excel (.xls, .xlsx)');
        this.$refs.fileInput.value = null;
        return;
      }

      this.file = f;
      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const wb = read(ev.target.result, { type: 'binary' });
          const ws = wb.Sheets[wb.SheetNames[0]];
          const rows = utils.sheet_to_json(ws);
          this.importRows = rows.map((r, idx) => ({
            index: idx,
            teacher_code: this.getCellValue(r, ['Mã giảng viên', 'ma_giang_vien', 'teacher_code']),
            category: this.getCellValue(r, ['Phân loại', 'phan_loai', 'category']),
          }));
        } catch (error) {
          this.$message.error('Không thể đọc file Excel. Vui lòng kiểm tra định dạng file.');
          this.clearFile();
        }
      };
      reader.readAsBinaryString(f);
    },
    async submit() {
      if (!this.importRows.length) return;
      this.loading = true;

      // 1) Frontend validation
      const frontResults = this.validateFront(this.importRows);
      // 2) Backend preview validation
      let backendResults = [];
      try {
        const normalizedRows = this.normalizeRows(this.importRows);
        const previewRes = await this.$store.dispatch(INSPECTION_TEACHER_PREVIEW_IMPORT, normalizedRows);
        const raw = this.extractPreviewRows(previewRes);
        backendResults = this.mapBackendResults(raw);
      } catch (e) {
        // If preview API fails, proceed with only frontend errors
        backendResults = [];
      }

      // 3) Merge errors and move to review step
      const merged = this.mergeResults(frontResults, backendResults);
      const hasErrors = merged.some(r => r.errors && r.errors.length);

      if (hasErrors) {
        this.results = merged;
        this.step = 2;
        this.loading = false;
        return;
      }
      await this.confirmImport();

    },
    async confirmImport() {
      this.loading = true;
      try {
        const res = await this.$store.dispatch(INSPECTION_TEACHER_CATEGORY_IMPORT, this.finalRows(this.importRows));
        if (res && !res.error) {
          this.$notify({ title: 'Thành công', message: 'Import phân loại giảng viên thành công', type: 'success' });
          this.$emit('done');
          this.visibleSync = false;
        } else {
          const message = (res && (res.message || res.error_message)) || 'Import thất bại';
          this.$notify({ title: 'Lỗi', message, type: 'error' });
        }
      } catch (error) {
        const message = (error && (error.message || (error.data && error.data.message))) || 'Import thất bại';
        this.$notify({ title: 'Lỗi', message, type: 'error' });
      } finally {
        this.loading = false;
      }
    },
    extractPreviewRows(previewRes) {
      if (!previewRes) return [];
      let raw = Array.isArray(previewRes)
        ? previewRes
        : (previewRes.data ?? previewRes.results ?? previewRes.rows ?? previewRes.dataImport ?? []);
      if (raw && !Array.isArray(raw) && typeof raw === 'object') {
        raw = Object.values(raw);
      }
      return Array.isArray(raw) ? raw : [];
    },
    normalizeRows(rows) {
      return rows.map(r => ({
        index: r.index,
        ma_giang_vien: (r.teacher_code || '').toString().trim(),
        phan_loai: r.category,
      }));
    },
    finalRows(rows) {
      return rows.map(r => ({
        index: r.index,
        ma_giang_vien: (r.teacher_code || '').toString().trim(),
        phan_loai: this.parseCategoryValue(r.category),
      }));
    },

    parseCategoryValue(value) {
      if (value === 1 || value === 2 || value === 3) return value;
      const v = this.normalizeString(value).toLowerCase();
      if (v === '1' || v === 'an toàn' || v === 'an toan') return 1;
      if (v === '2' || v === 'rủi ro' || v === 'rui ro') return 2;
      if (v === '3' || v === 'rủi ro cao' || v === 'rui ro cao') return 3;
      // '0' hoặc 'chưa phân loại' không hợp lệ theo backend
      return null;
    },
    validateFront(rows) {
      const seenCodes = new Set();
      const codeRegex = /^[A-Za-z0-9_-]+$/;
      return rows.map(r => {
        console.log(r);
        const errors = [];
        const rawCode = this.normalizeString(r.teacher_code);
        const rawCategory = this.normalizeString(r.category);
        console.log(rawCategory);

        // Kiểm tra dữ liệu trống (ưu tiên cao nhất)
        // const hasEmptyData = !rawCode || !this.normalizeString(rawCategory);
        if (!rawCode) {
          errors.push('Không được để trống mã giảng viên');
        } else if(!rawCategory) {
          errors.push('Không được để trống phân loại');
        } else {
          // Mã giảng viên: Kiểm tra tính hợp lệ
          if (!codeRegex.test(rawCode)) {
            errors.push('Mã giảng viên không hợp lệ');
          }
          
          // Mã giảng viên: Kiểm tra trùng lặp
          const codeKey = rawCode.toLowerCase();
          if (seenCodes.has(codeKey)) {
            errors.push('Mã giảng viên bị trùng trong file import');
          } else {
            seenCodes.add(codeKey);
          }

          // Phân loại: Kiểm tra giá trị hợp lệ
          const catVal = this.parseCategoryValue(rawCategory);
          if (catVal === null || catVal === undefined) {
            errors.push('Phân loại không hợp lệ');
          }
        }

        return {
          index: r.index,
          teacher_code: r.teacher_code,
          category: r.category,
          errors: errors,
          ok: errors.length === 0,
        };
      });
    },
    normalizeString(val) {
      if (val == null) return '';
      return String(val)
        .replace(/[\s\u00A0]+/g, ' ')
        .trim();
    },
    normalizeHeaderKey(key) {
      if (!key) return '';
      return String(key)
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/\s+/g, ' ')
        .trim();
    },
    getCellValue(row, preferredKeys = []) {
      // Try exact keys first
      for (const k of preferredKeys) {
        if (row[k] != null && row[k] !== '') return row[k];
      }
      // Try normalized keys (remove accents, trim, lowercase)
      const normalizedRow = {};
      Object.keys(row || {}).forEach((k) => {
        normalizedRow[this.normalizeHeaderKey(k)] = row[k];
      });
      const normalizedCandidates = preferredKeys.map(k => this.normalizeHeaderKey(k));
      for (const nk of normalizedCandidates) {
        if (normalizedRow[nk] != null && normalizedRow[nk] !== '') return normalizedRow[nk];
      }
      return '';
    },
    mapBackendResults(raw) {
      if (!Array.isArray(raw)) return [];
      return raw.map((item, idx) => {
        const errors = this.normalizeErrorList(item.errors || item.error || []);
        return {
          index: item.index != null ? item.index : idx,
          teacher_code: item.teacher_code || item.ma_giang_vien || item.code || '',
          category: (item.category != null ? item.category : (item.phan_loai != null ? item.phan_loai : '')),
          errors,
          ok: !(errors && errors.length),
        };
      });
    },
    normalizeErrorList(err) {
      if (!err) return [];
      if (Array.isArray(err)) return err.filter(Boolean).map(e => String(e));
      if (typeof err === 'string') return [err];
      if (typeof err === 'object') {
        const keys = Object.keys(err).filter(Boolean);
        const values = Object.values(err).flat().filter(Boolean).map(e => String(e));
        return Array.from(new Set([...keys, ...values]));
      }
      try { return [String(err)]; } catch (_) { return []; }
    },
    mergeResults(front, back) {
      const backByIndex = new Map(back.map(b => [b.index, b]));
      return front.map(f => {
        const b = backByIndex.get(f.index);
        const combinedErrors = [...(f.errors || [])];
        if (b && b.errors && b.errors.length) combinedErrors.push(...b.errors);
        
        // Loại bỏ lỗi trùng lặp sau khi merge
        const uniqueErrors = Array.from(new Set(combinedErrors));
        
        return {
          index: f.index,
          teacher_code: f.teacher_code || (b && (b.teacher_code || b.ma_giang_vien)) || '',
          category: f.category || (b && (b.category != null ? b.category : b.phan_loai)) || '',
          errors: uniqueErrors,
          ok: uniqueErrors.length === 0,
        };
      });
    },
  },
};
</script>

<style scoped>
.file-input__input {
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
}

.file-input__label {
  height: 34px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  padding: 7px 10px;
  box-shadow: 0 0 2px rgba(0, 0, 0, .25);
  background-color: #FF9900;

  &:hover {
    background-color: #e68a00;
  }

  &.is-disabled {
    background-color: #FF9900;
    opacity: 0.5;
    cursor: not-allowed;
    box-shadow: none;

    &:hover {
      background-color: #FF9900;
    }
  }

}
</style>
